import { JournalService } from '@affine/core/modules/journal';
import {
  describeRepeatRule,
  expandRepeatRule,
  formatRepeatRule,
  JOURNAL_DATE_FORMAT,
  JournalRepeatService,
  MAX_REPEAT_OCCURRENCES,
  parseRepeatRule,
  REPEAT_CONFIRM_THRESHOLD,
  type RepeatBound,
  type RepeatRule,
  type RepeatSeriesSummary,
  today,
} from '@affine/core/modules/journal-repeat';
import type { BlockRepeatMeta } from '@blocksuite/affine/model';
import { focusTextModel } from '@blocksuite/affine/rich-text';
import { NotificationProvider } from '@blocksuite/affine-shared/services';
import {
  SlashMenuConfigExtension,
  type SlashMenuActionContext,
  type SlashMenuActionItem,
  type SlashMenuContext,
} from '@blocksuite/affine/widgets/slash-menu';
import {
  CalendarXmarkIcon,
  MoveToIcon,
  TimelineIcon,
  TodayIcon,
  UpdatedIcon,
} from '@blocksuite/icons/lit';
import type { BlockModel, ExtensionType } from '@blocksuite/store';
import type { FrameworkProvider } from '@toeverything/infra';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';

/** Only these flavours carry the `repeat` prop. */
const REPEATABLE_FLAVOURS = new Set(['affine:list', 'affine:paragraph']);

const GRAMMAR_HINT =
  'Try: every 3 days for 2 months · every weekday for 1 month · every mon,wed until dec 31';

const ICON_SIZE = { width: '20px', height: '20px' } as const;

function shortDate(date: string) {
  return dayjs(date, JOURNAL_DATE_FORMAT).format('MMM D');
}

/**
 * Recognises an explicit `YYYY-MM-DD` argument. Checked by regex rather than
 * dayjs's strict mode, which needs the `customParseFormat` plugin that this
 * app does not load — without it the strict flag is silently ignored and
 * things like `12/31/2026` would slip through.
 */
function parseExplicitDate(input: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input)) return undefined;
  const parsed = dayjs(input, JOURNAL_DATE_FORMAT);
  return parsed.isValid() ? parsed : undefined;
}

function readRepeat(model: BlockModel): BlockRepeatMeta | undefined {
  const repeat = (model.props as { repeat?: BlockRepeatMeta | null }).repeat;
  return repeat && typeof repeat.id === 'string' ? repeat : undefined;
}

function isRepeatable(model: BlockModel) {
  return REPEATABLE_FLAVOURS.has(model.flavour);
}

/**
 * Matches a menu row against what the user has typed so far. Every picker
 * falls back to the unfiltered list when nothing matches — returning an empty
 * array from `getArgumentItems` drops the menu into `no_result`, and the next
 * space keypress would then close it mid-argument.
 */
function filterOrAll<T>(items: T[], argument: string, text: (item: T) => string) {
  const needle = argument.trim().toLowerCase();
  if (!needle) return items;
  const matched = items.filter(item => text(item).toLowerCase().includes(needle));
  return matched.length > 0 ? matched : items;
}

export function patchRepeatSlashMenu(
  framework: FrameworkProvider
): ExtensionType {
  const journalService = framework.get(JournalService);
  const repeatService = framework.get(JournalRepeatService);

  const notification = (ctx: SlashMenuContext) =>
    ctx.std.getOptional(NotificationProvider);

  const toast = (ctx: SlashMenuContext, message: string) => {
    notification(ctx)?.toast(message);
  };

  /**
   * Series are anchored to the journal date of the doc they were created in,
   * so `/repeat` typed in tomorrow's entry schedules from tomorrow. Outside a
   * journal there is no such date, so today stands in.
   */
  const anchorFor = (ctx: SlashMenuContext) =>
    journalService.journalDate$(ctx.std.store.id).value ?? today();

  const journalDateOf = (docId: string) =>
    journalService.journalDate$(docId).value;

  // ---------------------------------------------------------------- /repeat

  const PRESET_RULES = [
    'every day for 1 month',
    'every weekday for 1 month',
    'every week for 3 months',
    'every 2 weeks for 3 months',
    'every month for 1 year',
  ];

  const ALTERNATIVE_BOUNDS: RepeatBound[] = [
    { kind: 'duration', unit: 'weeks', amount: 2 },
    { kind: 'duration', unit: 'months', amount: 1 },
    { kind: 'duration', unit: 'months', amount: 3 },
    { kind: 'duration', unit: 'years', amount: 1 },
    { kind: 'times', count: 10 },
  ];

  /**
   * Finds the longest prefix of the argument that parses. Half-typed input
   * like `every 3 days for 2 mont` still yields `every 3 days`, which is
   * enough to offer useful completions instead of falling back to generic
   * presets.
   */
  const bestEffortParse = (argument: string, anchor: string) => {
    const tokens = argument.trim().split(/\s+/).filter(Boolean);
    for (let end = tokens.length; end > 0; end--) {
      const rule = parseRepeatRule(tokens.slice(0, end).join(' '), anchor);
      if (rule) return { rule, complete: end === tokens.length };
    }
    return null;
  };

  const describeSeries = (rule: RepeatRule, anchor: string) => {
    const { dates, truncated } = expandRepeatRule(rule, anchor);
    if (dates.length === 0) return { dates, label: 'No dates in range' };
    const range = `${shortDate(dates[0])} → ${shortDate(dates[dates.length - 1])}`;
    const cap = truncated ? ` · capped at ${MAX_REPEAT_OCCURRENCES}` : '';
    return {
      dates,
      label: `${dates.length} ${dates.length === 1 ? 'entry' : 'entries'} · ${range}${cap}`,
    };
  };

  const applyRepeat = (ctx: SlashMenuContext, rule: RepeatRule) => {
    const anchor = anchorFor(ctx);
    const { dates } = describeSeries(rule, anchor);
    if (dates.length === 0) {
      toast(ctx, `That rule has no dates after ${shortDate(anchor)}`);
      return;
    }

    const sourceDocId = ctx.std.store.id;
    const blockId = ctx.model.id;
    const canonical = formatRepeatRule(rule);

    const run = () => {
      const repeat: BlockRepeatMeta = {
        id: nanoid(),
        rule: canonical,
        anchor,
      };
      // Stamp the source line first so `/delete-repeat` works from it even if
      // writing the copies fails partway through.
      ctx.std.store.updateBlock(ctx.model, { repeat });

      repeatService
        .applyRepeat({ sourceDocId, blockId, dates, repeat })
        .then(({ written, failed }) => {
          if (written.length === 0) {
            toast(ctx, 'Could not write to any journal entry');
            return;
          }
          const range = `${shortDate(written[0])} → ${shortDate(written[written.length - 1])}`;
          const problem =
            failed.length > 0 ? ` (${failed.length} could not be written)` : '';
          notification(ctx)?.notify({
            title: `Repeated to ${written.length} ${written.length === 1 ? 'entry' : 'entries'}`,
            message: `${canonical} · ${range}${problem}`,
            accent: failed.length > 0 ? 'warning' : 'success',
            actions: [
              {
                key: 'undo',
                label: 'Undo',
                // Cross-doc writes are not in the editor undo stack, so Cmd+Z
                // will not reverse them — this is the only way back.
                onClick: () => {
                  repeatService
                    .deleteSeries({ seriesId: repeat.id, scope: 'all' })
                    .then(({ removed }) => {
                      clearRepeatMark(ctx);
                      toast(ctx, `Removed ${removed} repeated entries`);
                    })
                    .catch(console.error);
                },
              },
            ],
          });
        })
        .catch(error => {
          console.error(error);
          toast(ctx, 'Failed to create the repeat');
        });
    };

    if (dates.length > REPEAT_CONFIRM_THRESHOLD) {
      const confirm = notification(ctx)?.confirm;
      if (!confirm) {
        run();
        return;
      }
      notification(ctx)
        ?.confirm({
          title: `Repeat to ${dates.length} journal entries?`,
          message: `"${canonical}" will add this line to ${dates.length} entries between ${shortDate(dates[0])} and ${shortDate(dates[dates.length - 1])}, creating any that do not exist yet.`,
          confirmText: 'Repeat',
          cancelText: 'Cancel',
        })
        .then(confirmed => {
          if (confirmed) run();
        })
        .catch(console.error);
      return;
    }

    run();
  };

  const repeatRow = (rule: RepeatRule, anchor: string): SlashMenuActionItem => {
    const { label } = describeSeries(rule, anchor);
    return {
      name: describeRepeatRule(rule),
      description: label,
      icon: UpdatedIcon(ICON_SIZE),
      action: (actionCtx: SlashMenuContext) => applyRepeat(actionCtx, rule),
    };
  };

  const getRepeatSuggestions = (
    ctx: SlashMenuContext,
    actionContext: SlashMenuActionContext
  ): SlashMenuActionItem[] => {
    const anchor = anchorFor(ctx);
    const argument = actionContext.argument.trim();

    if (argument) {
      const attempt = bestEffortParse(argument, anchor);
      if (attempt?.complete) {
        return [repeatRow(attempt.rule, anchor)];
      }
      if (attempt) {
        // Partial input: offer the interval we understood, with a spread of
        // plausible bounds so the series can be picked without finishing the
        // sentence.
        const seen = new Set([formatRepeatRule(attempt.rule)]);
        const rows = [repeatRow(attempt.rule, anchor)];
        for (const bound of ALTERNATIVE_BOUNDS) {
          const candidate: RepeatRule = { interval: attempt.rule.interval, bound };
          const key = formatRepeatRule(candidate);
          if (seen.has(key)) continue;
          seen.add(key);
          rows.push(repeatRow(candidate, anchor));
        }
        return rows;
      }
    }

    const presets = PRESET_RULES.map(text => parseRepeatRule(text, anchor))
      .filter((rule): rule is RepeatRule => !!rule)
      .map(rule => repeatRow(rule, anchor));
    return filterOrAll(presets, argument, row => row.name);
  };

  // --------------------------------------------------------- /delete-repeat

  const clearRepeatMark = (ctx: SlashMenuContext) => {
    if (!ctx.std.store.hasBlock(ctx.model.id)) return;
    ctx.std.store.updateBlock(ctx.model, { repeat: null });
  };

  type DeleteScope = 'future' | 'all' | 'this';

  const runDeleteRepeat = (ctx: SlashMenuContext, scope: DeleteScope) => {
    const repeat = readRepeat(ctx.model);
    if (!repeat) {
      toast(ctx, 'This line is not part of a repeat');
      return;
    }

    if (scope === 'this') {
      clearRepeatMark(ctx);
      toast(ctx, 'Removed the repeat mark from this line');
      return;
    }

    repeatService
      .deleteSeries({
        seriesId: repeat.id,
        scope,
        skip: { docId: ctx.std.store.id, blockId: ctx.model.id },
      })
      .then(({ removed, docs }) => {
        clearRepeatMark(ctx);
        if (removed === 0) {
          toast(ctx, 'No other entries to remove');
          return;
        }
        toast(
          ctx,
          `Removed ${removed} ${removed === 1 ? 'entry' : 'entries'} across ${docs} ${docs === 1 ? 'day' : 'days'}`
        );
      })
      .catch(error => {
        console.error(error);
        toast(ctx, 'Failed to remove the repeat');
      });
  };

  const getDeleteRepeatSuggestions = (
    ctx: SlashMenuContext,
    actionContext: SlashMenuActionContext
  ): SlashMenuActionItem[] => {
    const repeat = readRepeat(ctx.model);
    const rule = repeat?.rule ?? 'this repeat';
    const options: { scope: DeleteScope; name: string; description: string }[] = [
      {
        scope: 'future',
        name: 'From tomorrow onward',
        description: `Remove upcoming copies of "${rule}"`,
      },
      {
        scope: 'all',
        name: 'All occurrences',
        description: 'Remove every copy, past entries included',
      },
      {
        scope: 'this',
        name: 'Only this line',
        description: 'Keep the line, drop its repeat mark',
      },
    ];

    return filterOrAll(options, actionContext.argument, option => option.name).map(
      option => ({
        name: option.name,
        description: option.description,
        icon: CalendarXmarkIcon(ICON_SIZE),
        action: (actionCtx: SlashMenuContext) =>
          runDeleteRepeat(actionCtx, option.scope),
      })
    );
  };

  // ------------------------------------------------------------------ /move

  const moveTargets = (anchor: string) => {
    const base = dayjs(anchor, JOURNAL_DATE_FORMAT);
    const relative = [
      { name: 'Tomorrow', date: base.add(1, 'day') },
      { name: 'In 2 days', date: base.add(2, 'day') },
      { name: 'In 3 days', date: base.add(3, 'day') },
      { name: 'Next week', date: base.add(7, 'day') },
      { name: 'Next month', date: base.add(1, 'month') },
      { name: 'Yesterday', date: base.subtract(1, 'day') },
    ];
    const weekdays = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ].map((label, weekday) => ({
      name: `Next ${label}`,
      date: base.add((weekday - base.day() + 7) % 7 || 7, 'day'),
    }));
    return [...relative, ...weekdays].map(entry => ({
      name: entry.name,
      date: entry.date.format(JOURNAL_DATE_FORMAT),
    }));
  };

  const runMove = (ctx: SlashMenuContext, date: string) => {
    const sourceDocId = ctx.std.store.id;
    const blockId = ctx.model.id;

    if (journalDateOf(sourceDocId) === date) {
      toast(ctx, 'This line is already on that day');
      return;
    }

    repeatService
      .moveBlockToJournal(sourceDocId, blockId, date)
      .then(() => {
        const store = ctx.std.store;
        if (!store.hasBlock(blockId)) return;
        const model = store.getModelById(blockId);
        if (!model) return;
        const previous = store.getPrev(model);
        store.deleteBlock(model);
        if (previous?.text) {
          focusTextModel(ctx.std, previous.id, previous.text.length);
        }
        toast(ctx, `Moved to ${shortDate(date)}`);
      })
      .catch(error => {
        console.error(error);
        toast(ctx, 'Failed to move the line');
      });
  };

  const getMoveSuggestions = (
    ctx: SlashMenuContext,
    actionContext: SlashMenuActionContext
  ): SlashMenuActionItem[] => {
    const anchor = anchorFor(ctx);
    const argument = actionContext.argument.trim();
    const rows: { name: string; date: string }[] = [];

    // An explicit date always wins, and sorts first.
    const explicit = parseExplicitDate(argument);
    if (explicit) {
      rows.push({
        name: explicit.format('MMM D, YYYY'),
        date: explicit.format(JOURNAL_DATE_FORMAT),
      });
    }

    rows.push(...filterOrAll(moveTargets(anchor), argument, entry => entry.name));

    return rows.map(entry => ({
      name: entry.name,
      description: entry.date,
      icon: MoveToIcon(ICON_SIZE),
      action: (actionCtx: SlashMenuContext) => runMove(actionCtx, entry.date),
    }));
  };

  // ----------------------------------------------------------------- /carry

  const runCarry = (ctx: SlashMenuContext) => {
    const targetDocId = ctx.std.store.id;
    const targetDate = journalDateOf(targetDocId);
    if (!targetDate) {
      toast(ctx, 'Carry over only works inside a journal entry');
      return;
    }

    const parent = ctx.std.store.getParent(ctx.model);
    const index = parent ? parent.children.indexOf(ctx.model) + 1 : undefined;

    repeatService
      .carryUnfinishedTodos({
        targetDocId,
        targetDate,
        parentId: parent?.id,
        index,
      })
      .then(({ carried, fromDate }) => {
        if (carried === 0) {
          toast(ctx, 'Nothing to carry over');
          return;
        }
        // The line `/carry` was typed on is left empty by the command; drop it
        // so the carried todos are not preceded by a blank paragraph.
        const store = ctx.std.store;
        const model = store.getModelById(ctx.model.id);
        if (model && model.text?.length === 0 && model.children.length === 0) {
          store.deleteBlock(model);
        }
        toast(
          ctx,
          `Carried ${carried} unfinished ${carried === 1 ? 'todo' : 'todos'} from ${fromDate ? shortDate(fromDate) : 'the previous entry'}`
        );
      })
      .catch(error => {
        console.error(error);
        toast(ctx, 'Failed to carry over todos');
      });
  };

  // --------------------------------------------------------------- /repeats

  const endSeries = (ctx: SlashMenuContext, series: RepeatSeriesSummary) => {
    repeatService
      .deleteSeries({ seriesId: series.id, scope: 'future' })
      .then(({ removed, docs }) => {
        if (removed === 0) {
          toast(ctx, 'That series had no upcoming entries left');
          return;
        }
        toast(
          ctx,
          `Ended "${series.rule}" · removed ${removed} ${removed === 1 ? 'entry' : 'entries'} across ${docs} ${docs === 1 ? 'day' : 'days'}`
        );
      })
      .catch(error => {
        console.error(error);
        toast(ctx, 'Failed to end the series');
      });
  };

  const getRepeatsSuggestions = (
    _ctx: SlashMenuContext,
    actionContext: SlashMenuActionContext
  ): SlashMenuActionItem[] => {
    repeatService.ensureSeriesIndex();
    const index = repeatService.seriesIndex$.value;

    if (index === null) {
      // The scan is async but this picker is not; the menu only re-filters on
      // input, so the next keystroke shows the results.
      return [
        {
          name: 'Scanning upcoming entries…',
          description: 'Type another character to show the results',
          icon: TimelineIcon(ICON_SIZE),
          action: () => {},
        },
      ];
    }

    if (index.length === 0) {
      return [
        {
          name: 'No active repeats',
          description: 'Nothing is repeating into upcoming entries',
          icon: TimelineIcon(ICON_SIZE),
          action: () => {},
        },
      ];
    }

    return filterOrAll(
      index,
      actionContext.argument,
      series => `${series.text} ${series.rule}`
    ).map(series => ({
      name: series.text.trim() || '(empty line)',
      description: `${series.rule} · ${series.count} upcoming · ends ${shortDate(series.lastDate)}`,
      icon: UpdatedIcon(ICON_SIZE),
      action: (actionCtx: SlashMenuContext) => endSeries(actionCtx, series),
    }));
  };

  // ------------------------------------------------------------------ items

  return SlashMenuConfigExtension('affine-repeat', {
    items: [
      {
        name: 'Repeat',
        icon: UpdatedIcon(ICON_SIZE),
        description: 'Repeat this line into future journal entries',
        group: '6_Date@20',
        searchAlias: ['repeat', 'every', 'recur', 'recurring'],
        acceptsArgument: true,
        when: ctx => isRepeatable(ctx.model),
        action: (ctx, actionContext) => {
          const anchor = anchorFor(ctx);
          const rule = actionContext?.argument
            ? parseRepeatRule(actionContext.argument, anchor)
            : null;
          if (!rule) {
            toast(ctx, GRAMMAR_HINT);
            return;
          }
          applyRepeat(ctx, rule);
        },
        getArgumentItems: getRepeatSuggestions,
      },
      {
        name: 'Delete repeat',
        icon: CalendarXmarkIcon(ICON_SIZE),
        description: 'Stop this line repeating into future entries',
        group: '6_Date@21',
        searchAlias: [
          'delete-repeat',
          'deleterepeat',
          'unrepeat',
          'norepeat',
          'stop repeat',
        ],
        acceptsArgument: true,
        when: ctx => isRepeatable(ctx.model) && !!readRepeat(ctx.model),
        action: ctx => runDeleteRepeat(ctx, 'future'),
        getArgumentItems: getDeleteRepeatSuggestions,
      },
      {
        name: 'Move to',
        icon: MoveToIcon(ICON_SIZE),
        description: 'Move this line to another journal day',
        group: '6_Date@22',
        searchAlias: ['move', 'moveto', 'push', 'defer'],
        acceptsArgument: true,
        when: ctx => isRepeatable(ctx.model),
        action: (ctx, actionContext) => {
          const anchor = anchorFor(ctx);
          const argument = actionContext?.argument.trim() ?? '';
          const explicit = parseExplicitDate(argument);
          if (explicit) {
            runMove(ctx, explicit.format(JOURNAL_DATE_FORMAT));
            return;
          }
          const match = moveTargets(anchor).find(
            entry => entry.name.toLowerCase() === argument.toLowerCase()
          );
          if (!match) {
            toast(ctx, 'Try: /move tomorrow, /move next monday, /move 2026-12-31');
            return;
          }
          runMove(ctx, match.date);
        },
        getArgumentItems: getMoveSuggestions,
      },
      {
        name: 'Carry over',
        icon: TodayIcon(ICON_SIZE),
        description: 'Pull unfinished todos from the previous journal entry',
        group: '6_Date@23',
        searchAlias: ['carry', 'carryover', 'rollover', 'unfinished'],
        when: ctx => !!journalDateOf(ctx.std.store.id),
        action: runCarry,
      },
      {
        name: 'Repeats',
        icon: TimelineIcon(ICON_SIZE),
        description: 'List and end active repeat series',
        group: '6_Date@24',
        searchAlias: ['repeats', 'listrepeats', 'managerepeats'],
        acceptsArgument: true,
        action: ctx => {
          repeatService.ensureSeriesIndex();
          const count = repeatService.seriesIndex$.value?.length;
          toast(
            ctx,
            count === undefined
              ? 'Scanning upcoming entries — run /repeats again in a moment'
              : `${count} active ${count === 1 ? 'series' : 'series'}`
          );
        },
        getArgumentItems: getRepeatsSuggestions,
      },
    ],
  });
}
